import { objectSpread2 as _objectSpread2 } from '../../_virtual/_rollupPluginBabelHelpers.js';
import isArray from 'lodash/isArray.js';
import clamp from 'lodash/clamp.js';
import PendoInputNumber from '../input-number/pendo-input-number.vue.js';
import Dot from './dot.vue.js';
import Mark from './mark.vue.js';
import labelsMixin from '../../mixins/labels.js';

var script = {
  name: 'PendoSlider',
  components: {
    PendoInputNumber,
    SliderMark: Mark,
    SliderDot: Dot
  },
  mixins: [labelsMixin],
  props: {
    value: {
      type: Number,
      default: 0
    },
    min: {
      type: Number,
      default: 0
    },
    max: {
      type: Number,
      default: 100
    },
    step: {
      type: Number,
      default: 1,
      validator: step => step > 0
    },
    showInput: {
      type: Boolean,
      default: false
    },
    inputSize: {
      type: String,
      default: 'medium',
      validator: inputSize => ['mini', 'small', 'medium'].includes(inputSize)
    },
    showStops: {
      type: Boolean,
      default: false
    },
    showTooltip: {
      type: Boolean,
      default: false
    },
    formatTooltip: {
      type: Function,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    vertical: {
      type: Boolean,
      default: false
    },
    height: {
      type: String,
      default: '0px'
    },
    marks: {
      type: [Object, Array],
      default: null
    },
    showProcess: {
      type: Boolean,
      default: false
    },
    processColor: {
      type: Object,
      default: null
    },
    lazy: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: 'line',
      validator: type => ['line', 'bar'].includes(type)
    }
  },
  data() {
    return {
      isDragging: false,
      dragValue: null,
      sliderSize: 1,
      defaultBorderColor: '#dadce5'
    };
  },
  computed: {
    stops() {
      if (!this.showStops || this.min > this.max) {
        return [];
      }
      const stopCount = (this.max - this.min) / this.step;
      const stepWidth = 100 * this.step / (this.max - this.min);
      const result = [];
      for (let i = 1; i < stopCount; i++) {
        result.push(i * stepWidth);
      }
      return result.filter(step => step > 100 * (this.model - this.min) / (this.max - this.min));
    },
    markList() {
      if (!this.marks) {
        return [];
      }
      const isMarksAnArray = isArray(this.marks);
      const marksKeys = isMarksAnArray ? this.marks : Object.keys(this.marks);
      return marksKeys.map(parseFloat).sort((a, b) => a - b).filter(point => point <= this.max && point >= this.min).map((point, index) => {
        return {
          point,
          position: (point - this.min) * 100 / (this.max - this.min),
          mark: isMarksAnArray ? this.marks[index] : this.marks[point]
        };
      });
    },
    precision() {
      const precisions = [this.min, this.max, this.step].map(item => {
        const decimal = `${item}`.split('.')[1];
        return decimal ? decimal.length : 0;
      });
      return Math.max.apply(null, precisions);
    },
    processColors() {
      const colorDarkBlue = '#128297';
      const colorLightBlue = '#95dffb';
      const barDefault = {
        borderColor: colorDarkBlue,
        backgroundColor: colorLightBlue
      };
      const lineDefault = {
        backgroundColor: colorDarkBlue
      };
      const defaultStyle = this.type === 'bar' ? barDefault : lineDefault;
      return Object.assign({}, defaultStyle, this.processColor ? this.processColor : null);
    },
    processStyle() {
      const barSize = `${100 * (this.model - this.min) / (this.max - this.min)}%`;
      const barStart = '0%';
      if (this.vertical) {
        return _objectSpread2(_objectSpread2({}, this.processColors), {}, {
          height: barSize,
          bottom: barStart
        });
      }
      return _objectSpread2(_objectSpread2({}, this.processColors), {}, {
        width: barSize,
        left: barStart
      });
    },
    inputValue: {
      get() {
        if (this.isDragging) {
          return this.model;
        }
        return this.value;
      },
      set(val) {
        this.updateModel(val);
      }
    },
    model: {
      get() {
        if (this.isDragging) {
          return this.dragValue;
        }
        if (typeof this.value !== 'number' || isNaN(this.value)) {
          return this.min;
        }
        return clamp(this.value, this.min, this.max);
      },
      set(value) {
        const normalized = parseFloat(value.toFixed(this.precision));
        if (this.isDragging) {
          this.dragValue = value;
          if (this.lazy) {
            return;
          }
        }
        this.$emit('input', normalized);
      }
    },
    ariaLabel() {
      if (this.$attrs && this.$attrs['aria-label']) {
        return this.$attrs['aria-label'];
      }
      return this.topLabel || this.bottomLabel || this.inlineLabel || undefined;
    }
  },
  mounted() {
    this.resetSize();
    window.addEventListener('resize', this.resetSize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resetSize);
  },
  methods: {
    setDotPositionFromClick(percent) {
      const targetValue = this.min + percent * (this.max - this.min) / 100;
      this.updateModel(targetValue);
    },
    onSliderClick(_ref) {
      let {
        clientX,
        clientY
      } = _ref;
      if (this.disabled || this.isDragging) {
        return;
      }
      this.resetSize();
      const {
        left,
        bottom
      } = this.$refs.slider.getBoundingClientRect();
      if (this.vertical) {
        this.setDotPositionFromClick((bottom - clientY) / this.sliderSize * 100);
        return;
      }
      this.setDotPositionFromClick((clientX - left) / this.sliderSize * 100);
    },
    resetSize() {
      if (this.$refs.slider) {
        this.sliderSize = this.$refs.slider[`client${this.vertical ? 'Height' : 'Width'}`];
      }
    },
    getStopStyle(position) {
      if (this.vertical) {
        return {
          bottom: `${position}%`
        };
      }
      return {
        left: `${position}%`
      };
    },
    getMarkStyle(mark) {
      const style = this.getStopStyle(mark.position);
      const processColor = this.showProcess ? this.processColors.borderColor : this.defaultBorderColor;
      style['background-color'] = mark.position < this.model ? processColor : this.defaultBorderColor;
      return style;
    },
    updateModel(value) {
      this.model = value;
    },
    onDragStart() {
      this.dragValue = this.model;
      this.isDragging = true;
      document.body.style.cursor = this.vertical ? 'ns-resize' : 'ew-resize';
      this.$emit('drag-start');
    },
    onDrag(value) {
      this.updateModel(value);
      this.$emit('drag');
      this.resetSize();
    },
    onDragEnd(value) {
      this.isDragging = false;
      document.body.style.cursor = 'unset';
      this.updateModel(value);
      this.$emit('drag-end');
    }
  }
};

export { script as default };
//# sourceMappingURL=pendo-slider.vue2.js.map
